import type { AllocationResult, Decision, Signal } from '@whaletail/signal-engine';
import { countdownText } from '@whaletail/signal-engine';
import type { AccuracyWindows } from './btc15History';
import { formatPercent, formatSignedPercent, formatSignedUsd, formatUsd } from './format';

/** The live model's paper-bankroll record, from the bankroll ledger (the same figures the Track record tab shows). */
export interface CopySummaryRecord {
  /** P&L after fees across every run. */
  pnl: number;
  /** The current run's number; 0 before any settled round. */
  runCount: number;
  busts: number;
  /** What the current run has left. */
  bankroll: number;
}

/**
 * Builds the plain-text copy summary for the current call, matching the
 * Scriptable widget's own section headings and terminology (ROUND, MODEL,
 * MARKET & SIZING, WHY, WATCH, ACCURACY, STRATEGY P/L) so a summary copied
 * from the web page reads the same as one copied from the phone widget.
 */
export function buildCopySummary(
  signal: Signal,
  decision: Decision,
  allocation: AllocationResult,
  accuracy: AccuracyWindows,
  record: CopySummaryRecord,
  secondsRemaining: number | null,
): string {
  const lines: string[] = [];

  lines.push(
    `Model call: ${decision.recommendation} · ${formatPercent(decision.probability, 1)} model probability`,
  );
  lines.push('');
  lines.push('ROUND');
  lines.push(
    `${formatUsd(signal.roundOpenPrice)} → ${formatUsd(signal.price)}  ${formatSignedPercent(signal.distancePct)}`,
  );
  lines.push(
    secondsRemaining !== null ? `${countdownText(secondsRemaining)} remaining` : '',
  );
  lines.push('');
  lines.push('MODEL');
  lines.push(
    `${signal.action} · ${formatPercent(decision.probability, 1)} · ${signal.regime}`,
  );
  lines.push(
    `Score ${signal.rawScore.toFixed(1)} · trend ${signal.trendKey} · RSI ${signal.rsi.toFixed(0)}`,
  );
  lines.push('');
  lines.push('MARKET & SIZING');
  lines.push(
    allocation.stake > 0
      ? `Simulated stake: ${formatUsd(allocation.stake)} (${allocation.reason})`
      : `Simulated stake: $0.00 (${allocation.reason})`,
  );
  lines.push('');
  lines.push('ACCURACY');
  lines.push(
    accuracy.windows
      .map(
        ({ n, stats }) =>
          `${n}: ${stats.total > 0 ? formatPercent(stats.rate ?? undefined, 0) : '—'}`,
      )
      .join(' · '),
  );
  lines.push(
    `Overall ${formatPercent(accuracy.overall.rate ?? undefined, 1)} · ${accuracy.overall.total} settled`,
  );
  lines.push('');
  lines.push('STRATEGY P/L');
  lines.push(
    record.runCount === 0
      ? 'No settled bets yet'
      : `${formatSignedUsd(record.pnl)} after fees, all runs · run ${record.runCount}, ${
          record.busts === 1 ? '1 bust' : `${record.busts} busts`
        } · ${formatUsd(record.bankroll)} left`,
  );
  lines.push(
    'SKIP calls are excluded. Paper simulation — not financial or betting advice.',
  );

  return lines.filter((line, i) => line !== '' || lines[i - 1] !== '').join('\n');
}
